import type { AxiosInstance } from 'axios';

import type { AuthService } from 'types/AuthService';

import type { OnboardingComposition, WizardUseCases, ConfigUseCases, PlacesUseCases } from './types';
import { createWizardUseCases } from './factories/createWizardUseCases';
import { createConfigUseCases } from './factories/createConfigUseCases';
import { createPlacesUseCases } from './factories/createPlacesUseCases';

export type {
  OnboardingComposition,
  OnboardingUseCases,
  WizardUseCases,
  ConfigUseCases,
  PlacesUseCases,
} from './types';

const createOnboardingComposition = (
  httpClient: AxiosInstance,
  payHttpClient: AxiosInstance,
  adminHttpClient: AxiosInstance,
  authService: AuthService,
): OnboardingComposition => ({
  onboardingUseCases: {
    wizardUseCases: createWizardUseCases(httpClient, payHttpClient, adminHttpClient, authService),
    configUseCases: createConfigUseCases(httpClient, payHttpClient, adminHttpClient, authService),
    placesUseCases: createPlacesUseCases(),
  },
});

let composition: OnboardingComposition | null = null;

/**
 * Initializes the Onboarding domain Composition Root.
 * MUST be called ONCE in app bootstrap, before any hook uses it.
 *
 * @example
 * // In App/bootstrap.ts
 * initOnboardingComposition(axiosInstance, payAxiosInstance, authService);
 */
export const initOnboardingComposition = (
  httpClient: AxiosInstance,
  payHttpClient: AxiosInstance,
  adminHttpClient: AxiosInstance,
  authService: AuthService,
): void => {
  if (composition !== null) return;
  composition = createOnboardingComposition(httpClient, payHttpClient, adminHttpClient, authService);
};

/**
 * Returns the initialized composition singleton.
 *
 * @throws Error if called before initOnboardingComposition
 */
export const getOnboardingComposition = (): OnboardingComposition => {
  if (composition === null) {
    throw new Error('[Onboarding] Composition not initialized. Call initOnboardingComposition() in bootstrap.');
  }
  return composition;
};

/** @internal For testing only */
export const resetOnboardingComposition = (): void => {
  composition = null;
};

/**
 * Gets wizard use cases (fetch status, complete wizard).
 *
 * @example
 * const { completeWizard } = getWizardUseCases();
 * await completeWizard.execute();
 */
export const getWizardUseCases = (): WizardUseCases => getOnboardingComposition().onboardingUseCases.wizardUseCases;

/**
 * Gets step configuration use cases (apply table service, etc.).
 *
 * @example
 * const { applyTableService } = getConfigUseCases();
 * await applyTableService.execute({ hasTableService: true, areas: [...] });
 */
export const getConfigUseCases = (): ConfigUseCases => getOnboardingComposition().onboardingUseCases.configUseCases;

/**
 * Gets places use cases (address autocomplete + details).
 *
 * @example
 * const { fetchPredictions, getPlaceDetails } = getPlacesUseCases();
 * const predictions = await fetchPredictions.execute({ input: 'Av Insurgentes', country: 'mx' });
 */
export const getPlacesUseCases = (): PlacesUseCases => getOnboardingComposition().onboardingUseCases.placesUseCases;
